import { ODSIconName } from "@/atoms/ODSIcons/ODSIcon.types";
import { ODSTextFieldProps } from "@/atoms/ODSTextField/ODSTextField.types";
import classNames from "classnames";
import React, {
  forwardRef,
  memo,
  useEffect,
  useId,
  useRef,
  useState,
} from "react";

import { ODSColumn } from "../ODSColumn";
import ODSIcon from "../ODSIcons/ODSIcon";
import { ODSRow } from "../ODSRow";
import ODSSupportMessage from "../ODSSupportMessage/ODSSupportMessage";
import ODSText from "../ODSText/ODSText";

import "./css/index.css";

/* TODO This component needs refactor, use less css classes, remove mode
 */
const ODSTextField = forwardRef<HTMLInputElement, ODSTextFieldProps>(
  (
    {
      type = "text",
      value,
      labelText,
      supportMessage = "",
      disabled = false,
      mode = "informative",
      id,
      prefixText = "",
      showCounter,
      suffixText = "",
      required = false,
      rightIconValue,
      leftIconValue = "",
      maxLength,
      placeholder,
      className,
      onFocus = () => {},
      onBlur = () => {},
      onChange = () => {},
      rightIconClickHandler,
      counterLimitMessage,
      rightIconAriaLabel,
      readOnly = false,
      containerProps,
      supportMessageProps,
      size = "large",
      ...props
    },
    ref
  ) => {
    const [hasFocus, setHasFocus] = useState(false);
    const [inputValue, setInputValue] = useState(value ? String(value) : "");
    const myRef = useRef<HTMLInputElement | null>(null);
    const autoGeneratedId = useId();
    const inputId = id ?? autoGeneratedId;
    const containerId = `${useId()}-container`;
    const rightContainerId = `${useId()}-right-container`;
    const supportMessageId = `${React.useId()}-supportMessage`;
    const counterId = `${React.useId()}-counter`;
    const prefixId = `${React.useId()}-prefix`;
    const suffixId = `${React.useId()}-suffix`;
    const showSupportMessage = supportMessage && mode !== "standard";

    const ODSTextField_InputfieldClassname = classNames(
      `ODSTextField-InputField`,
      `ODSTextField-InputField--supportMessage-${mode}`,
      `ODSTextField-InputField--mode-${mode}`,
      `ODSTextField-InputField--size-${size}`,
      {
        "ODSTextField-InputField--disabled": disabled,
        "ODSTextField-InputField--filled":
          Boolean(inputValue) ||
          Boolean(myRef?.current?.value) ||
          placeholder ||
          type === "number",
        "ODSTextField-InputField--readonly": readOnly,
        "ODSTextField-InputValue": !disabled,
      }
    );

    const ODSTextField_Container = classNames(
      `ODSTextField`,
      `ODSTextField--mode-${mode}`,
      `ODSTextField--size-${size}`,
      {
        "ODSTextField__has-focus": hasFocus,
        "ODSTextField--filled":
          Boolean(inputValue) ||
          Boolean(myRef?.current?.value) ||
          Boolean(placeholder) ||
          type === "number",
        "ODSTextField--disabled": disabled,
        "ODSTextField--has-leftIcon": Boolean(leftIconValue),
        "ODSTextField--has-prefix": Boolean(prefixText),
        "ODSTextField--has-rightIcon": Boolean(rightIconValue),
        "ODSTextField--has-suffix": Boolean(suffixText),
        "ODSTextField--readonly": readOnly,
      },
      className
    );

    useEffect(() => setInputValue(value ? String(value) : ""), [value]);

    useEffect(() => {
      const inputField = document.getElementById(inputId);
      const leftContainer = document.getElementById(containerId);
      const rightContainer = document.getElementById(rightContainerId);

      if (inputField && leftContainer) {
        if (prefixText.length > 2) {
          inputField.style["paddingLeft"] = `${
            leftContainer?.clientWidth + leftContainer?.offsetLeft
          }px `;
          // When prefix length is less than 2 and it has previous padding
        } else if (inputField.style?.paddingLeft) {
          inputField.style["paddingLeft"] = `${
            leftContainer?.clientWidth +
            leftContainer?.offsetLeft +
            (prefixText ? 8 : 0)
          }px `;
        }

        if (suffixText.length > 3 && rightContainer) {
          inputField.style["paddingRight"] = `${
            rightContainer?.clientWidth + leftContainer?.offsetLeft
          }px `;
          // When suffix length is less than 3 and it has previous padding
        } else if (inputField.style?.paddingRight && rightContainer) {
          inputField.style["paddingRight"] = `${
            rightContainer?.clientWidth +
            leftContainer?.offsetLeft +
            (suffixText ? 8 : 0)
          }px `;
        }
      }
    }, [prefixText, suffixText, hasFocus, placeholder, inputId, containerId]);

    useEffect(() => {
      if (disabled) {
        setInputValue("");
        setHasFocus(false);
        if (myRef.current) myRef.current.value = "";
      }
    }, [disabled]);

    const handleFocus = (
      event: React.FocusEvent<HTMLInputElement, Element>
    ) => {
      onFocus(event);
      setHasFocus(true);
    };

    const handleBlur = (event: React.FocusEvent<HTMLInputElement, Element>) => {
      onBlur(event);
      setHasFocus(false);
    };

    const handleChange = (event: React.ChangeEvent<HTMLInputElement>) => {
      onChange(event);
      setInputValue(event.target.value);
    };

    const shouldLabelFloating = () => {
      return (
        hasFocus ||
        Boolean(inputValue) ||
        Boolean(myRef?.current?.value) ||
        Boolean(placeholder)
      );
    };

    const rightIcon = () => (
      <ODSIcon
        name={rightIconValue as ODSIconName}
        decorative
        className={classNames(
          `ODSTextField-RightIconValue`,
          `ODSTextField-RightIconValue--mode-${mode}`,
          `ODSTextField-RightIconValue--size-${size}`
        )}
      />
    );

    return (
      <div className={ODSTextField_Container} {...containerProps}>
        <ODSColumn
          className={classNames(
            `ODSTextField-Content`,
            `ODSTextField-Content--size-${size}`
          )}
        >
          <label className="ODSTextField-Label" htmlFor={inputId}>
            <span className="ODSTextField-LabelText">{labelText}</span>
            {required && (
              <span className="ODSTextField-Required" aria-hidden="true">
                *
              </span>
            )}
          </label>
          {shouldLabelFloating() && (
            <div className="ODSTextField-LeftIcon--Container" id={containerId}>
              {leftIconValue && (
                <ODSIcon
                  name={leftIconValue as ODSIconName}
                  decorative
                  className="ODSTextField-LeftIconValue"
                />
              )}
              {prefixText && (
                <ODSText
                  as="span"
                  className="ODSTextField-Prefix"
                  id={prefixId}
                >
                  {prefixText}
                </ODSText>
              )}
            </div>
          )}
          {(rightIconValue || suffixText) && (
            <div
              className="ODSTextField-RightIcon--Container"
              id={rightContainerId}
            >
              {shouldLabelFloating() && suffixText && (
                <ODSText
                  as="span"
                  className="ODSTextField-Suffix"
                  id={suffixId}
                >
                  {suffixText}
                </ODSText>
              )}
              {rightIconValue &&
                (!rightIconClickHandler ? (
                  rightIcon()
                ) : (
                  <button
                    className="ODSTextField--Clickable-Icon"
                    onClick={(e) => rightIconClickHandler(e)}
                    aria-label={`${rightIconAriaLabel || rightIconValue}`}
                    type="button"
                  >
                    {rightIcon()}
                  </button>
                ))}
            </div>
          )}
          <input
            className={ODSTextField_InputfieldClassname}
            disabled={disabled}
            ref={(element) => {
              myRef.current = element;
              if (typeof ref === "function") {
                ref(element);
              } else if (ref) {
                ref.current = element;
              }
            }}
            id={inputId}
            value={inputValue}
            maxLength={maxLength}
            placeholder={placeholder}
            required={required}
            aria-required={required ? "true" : undefined}
            onFocus={(event) => handleFocus(event)}
            onBlur={(event) => handleBlur(event)}
            onChange={(event) => handleChange(event)}
            aria-describedby={
              supportMessage || showCounter || prefixText || suffixText
                ? `${supportMessageId} ${counterId} ${prefixId} ${suffixId}`
                : undefined
            }
            type={type}
            readOnly={readOnly}
            {...props}
          />
        </ODSColumn>
        {supportMessage || showCounter ? (
          <ODSRow as="div" className="ODSTextField-Texts-Container">
            {showSupportMessage ? (
              <ODSText
                as="span"
                className={classNames(
                  `ODSTextField-SupportText`,
                  `ODSTextField-SupportText--mode-${mode}`,
                  `ODSTextField-SupportText--size-${size}`
                )}
              >
                <ODSSupportMessage
                  {...supportMessageProps}
                  mode={mode}
                  helperText={supportMessage}
                  disabled={disabled}
                  id={supportMessageId}
                />
              </ODSText>
            ) : null}
            {showCounter ? (
              <ODSText
                as="span"
                className="ODSTextField-Counter"
                id={counterId}
              >
                {maxLength
                  ? `${inputValue.length}/${maxLength}`
                  : inputValue.length}
              </ODSText>
            ) : null}
            {inputValue.length === maxLength ? (
              <span className="sr-only" aria-live="assertive" role="alert">
                {counterLimitMessage}
              </span>
            ) : null}
          </ODSRow>
        ) : null}
      </div>
    );
  }
);

ODSTextField.displayName = "ODSTextField";

export default memo(ODSTextField);
